import { sportAccent } from '@ownermode/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { GUEST_COPY } from '@ownermode/shared';
import { useState } from 'react';
import { errorText } from '../components/kit';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { product, usePublicConfig } from '../lib/config';
import { usePageTitle } from './Public';

/** Real screens from the web build (fictional test league), captured by e2e/marketing-screens. */
const SHOT = {
  headquarters: {
    src: '/screens/web/headquarters.jpg',
    alt: 'Franchise Headquarters: valuation, cash, cap space and the decisions waiting on the owner',
  },
  negotiation: {
    src: '/screens/web/negotiation.jpg',
    alt: 'A negotiation with a city council: the conversation on the left, adjustable deal terms on the right',
  },
  draft: {
    src: '/screens/web/draft.jpg',
    alt: "The draft room: the general manager's three recommendations above the prospect board",
  },
  finances: {
    src: '/screens/web/finances.jpg',
    alt: 'Financials: revenue and costs for the season, round by round',
  },
  daily: {
    src: '/screens/web/daily.jpg',
    alt: "The Owner's Daily front page, with headlines from the league's latest week",
  },
  achievements: {
    src: '/screens/web/achievements.jpg',
    alt: 'Achievements earned from what happened in the owner’s leagues',
  },
} as const;

const W = 1440;
const H = 836;

function Shot({ shot, eager = false }: { shot: { src: string; alt: string }; eager?: boolean }) {
  return (
    <div className="overflow-hidden rounded-xl border border-overlay/10 bg-navy-900 shadow-2xl shadow-black/40">
      <img
        src={shot.src}
        alt={shot.alt}
        width={W}
        height={H}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        {...(eager ? { fetchPriority: 'high' as const } : {})}
        className="block h-auto w-full"
      />
    </div>
  );
}

const EXPERIENCE = [
  [
    'Run the franchise',
    'Set the direction, hire the front office and answer what lands on your desk every week.',
  ],
  [
    'Make the money decisions',
    'Ticket, media, sponsor and merchandise revenue against payroll and upkeep. Every dollar is in a ledger.',
  ],
  [
    'Negotiate with stakeholders',
    'Mayors, agents, sponsors and lenders — in your own words. They remember how you treated them.',
  ],
  [
    'Sign athletes and make trades',
    'Free agents, extensions, trades with draft picks and cash, or transfer bids in soccer.',
  ],
  [
    'Build your venue',
    'Renovate or replace your stadium or arena, financed with cash, debt or a deal with the city.',
  ],
  [
    'Chase championships',
    'Every game is simulated by the league office. Build a contender and win the title.',
  ],
  [
    'Grow your valuation',
    'Your franchise value moves every round, and the game tells you exactly why.',
  ],
] as const;

const SPORTS = [
  {
    id: 'football',
    name: 'Football',
    facts: ['Stadium', 'Salary cap · $200M', 'Draft', '19-man rosters', 'Four-team playoff'],
    blurb: 'Quarterback-driven contenders, a hard cap and an eight-week sprint to the playoffs.',
  },
  {
    id: 'basketball',
    name: 'Basketball',
    facts: ['Arena', 'Salary cap · $160M', 'Draft', '10-man rosters', 'Four-team playoff'],
    blurb: 'One star contract can make your season — or break your cap.',
  },
  {
    id: 'soccer',
    name: 'Soccer',
    facts: ['Stadium', 'Wage budget', 'Transfer windows', '20-man squads', 'League table'],
    blurb: 'A 14-matchday double round robin where every point counts and every bid is a gamble.',
  },
] as const;

const MODES = [
  {
    title: 'Solo',
    body: 'Take on seven CPU ownership groups, each with its own personality — cautious, aggressive, win-now.',
  },
  {
    title: 'Private leagues',
    body: 'Invite up to seven friends with a code. Any club nobody claims is run by a CPU owner.',
  },
  {
    title: 'Your pace',
    body: 'Manual: the commissioner advances. Live: the league runs on a clock. Async: it moves when every owner is ready, or at the deadline.',
  },
] as const;

interface Showcase {
  accessMode: 'development' | 'beta' | 'public';
  stats: { owners: number; leagues: number; gamesPlayed: number };
  updates: { date: string; title: string; body: string; tags: string[] }[];
}

const MODE_NOTE: Record<Showcase['accessMode'], string> = {
  development: 'In development — accounts are limited to testers.',
  beta: 'Private beta — free to play, no pay-to-win. Join with an invite code.',
  public: 'Open Public Beta — free to play, no pay-to-win. No invite needed.',
};

/** Below this the numbers say "empty" more than "alive"; the section stays hidden. */
const STATS_MIN_OWNERS = 25;

const linkBtn =
  'inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3 text-base font-semibold transition';
const goldBtn = `${linkBtn} bg-gold text-ink hover:bg-gold-bright shadow-[0_8px_30px_-12px_rgba(201,162,39,0.6)]`;
const outlineBtn = `${linkBtn} border border-gold/50 text-gold-bright hover:bg-gold/10`;

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-text">{children}</p>
  );
}

function Feature({
  id,
  eyebrow,
  title,
  children,
  shot,
  flip = false,
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: ReactNode;
  shot: { src: string; alt: string };
  flip?: boolean;
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:px-6 lg:grid-cols-5 lg:gap-12"
    >
      <div className={`lg:col-span-2 ${flip ? 'lg:order-2' : ''}`}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 id={`${id}-heading`} className="mt-3 text-3xl sm:text-4xl">
          {title}
        </h2>
        <div className="mt-4 space-y-3 text-silver">{children}</div>
      </div>
      <div className={`lg:col-span-3 ${flip ? 'lg:order-1' : ''}`}>
        <Shot shot={shot} />
      </div>
    </section>
  );
}

export function Landing() {
  usePageTitle();
  const { status, user, startGuest } = useAuth();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const config = usePublicConfig();
  const showcase = useQuery({
    queryKey: ['showcase'],
    queryFn: () => api<Showcase>('/api/public/showcase', { auth: false }),
    staleTime: 5 * 60_000,
  });
  const mode = config.data?.accessMode ?? (config.appMode === 'waitlist' ? 'beta' : 'public');
  const reservations = config.data?.reservationsEnabled ?? false;
  const join = mode !== 'public' && reservations ? '/waitlist' : '/register';
  const stats = showcase.data?.stats;
  const guestPlay = config.data?.guestPlay?.enabled ?? false;
  // Returning owners go straight back to their game; guests resume their franchise.
  if (status === 'signed_in' && user)
    return <Navigate to={user.isGuest ? '/play' : '/dashboard'} replace />;

  async function startPlaying() {
    setStarting(true);
    setStartError(null);
    try {
      await startGuest();
      navigate('/play/sport');
    } catch (err) {
      setStartError(errorText(err));
      setStarting(false);
    }
  }

  return (
    <>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-14 pt-12 sm:px-6 lg:grid-cols-2 lg:pb-20 lg:pt-20">
          <div>
            <Eyebrow>{GUEST_COPY.welcomeHeadline(config.productName)}</Eyebrow>
            <h1 className="mt-4 text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              You&rsquo;re not the coach. <br />
              <span className="text-gold-bright">You&rsquo;re the owner.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-silver">
              Take control of a football, basketball or soccer franchise. Build a championship team,
              negotiate major deals, develop your stadium or arena, and grow your club into a
              billion-dollar organization.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {guestPlay ? (
                <>
                  <button
                    type="button"
                    className={goldBtn}
                    onClick={() => void startPlaying()}
                    disabled={starting}
                  >
                    {starting ? 'Opening your boardroom…' : GUEST_COPY.start}
                  </button>
                  <Link to="/login" className={outlineBtn}>
                    {GUEST_COPY.signIn}
                  </Link>
                </>
              ) : (
                <>
                  <Link to={join} className={goldBtn}>
                    Become an Owner
                  </Link>
                  <a href="#how" className={outlineBtn}>
                    See how it works
                  </a>
                </>
              )}
            </div>
            {startError ? (
              <p role="alert" className="mt-3 text-sm text-negative">
                {startError}
              </p>
            ) : null}
            <p className="mt-4 text-sm text-slate" data-testid="access-mode">
              {MODE_NOTE[mode]}{' '}
              {guestPlay ? (
                <a href="#how" className="text-gold-bright underline underline-offset-2">
                  {GUEST_COPY.howItWorks}
                </a>
              ) : (
                <Link to="/login" className="text-gold-bright underline underline-offset-2">
                  Already an owner? Sign in
                </Link>
              )}
            </p>
          </div>
          <Shot shot={SHOT.headquarters} eager />
        </div>
      </section>

      <div className="gold-rule mx-auto max-w-5xl" />

      <section
        id="how"
        aria-labelledby="how-heading"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6"
      >
        <Eyebrow>The ownership experience</Eyebrow>
        <h2 id="how-heading" className="mt-3 text-3xl sm:text-4xl">
          Every decision that matters is yours.
        </h2>
        <p className="mt-3 max-w-2xl text-silver">
          You don&rsquo;t call plays. You decide who runs the club, what it spends, which deals it
          signs and what it&rsquo;s worth.
        </p>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {EXPERIENCE.map(([title, body], i) => (
            <li key={title} className="panel p-5">
              <span className="font-mono text-sm text-gold-text">0{i + 1}</span>
              <h3 className="mt-2 text-lg">{title}</h3>
              <p className="mt-1.5 text-sm text-silver">{body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section
        id="sports"
        aria-labelledby="sports-heading"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6"
      >
        <Eyebrow>Choose your sport</Eyebrow>
        <h2 id="sports-heading" className="mt-3 text-3xl sm:text-4xl">
          Three sports. Three different businesses.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {SPORTS.map((s) => (
            <article key={s.id} className="panel p-6">
              <div className="h-1 w-12 rounded" style={{ background: sportAccent[s.id] }} />
              <h3 className="mt-4 text-2xl">{s.name}</h3>
              <p className="mt-3 text-silver">{s.blurb}</p>
              <ul className="mt-5 flex flex-wrap gap-2" aria-label={`${s.name} at a glance`}>
                {s.facts.map((f) => (
                  <li
                    key={f}
                    className="rounded-full border border-overlay/10 px-2.5 py-1 text-xs text-silver"
                  >
                    {f}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate">
          Each league has eight fictional franchises. Every team, athlete and city is made up.
        </p>
      </section>

      <Feature
        id="desk"
        eyebrow="The ownership desk"
        title="Negotiate in your own words."
        shot={SHOT.negotiation}
      >
        <p>
          A city council wants a youth sports complex. A star&rsquo;s agent wants a raise. A lender
          wants collateral. Talk it through, move the terms, and sign — or walk away.
        </p>
        <p>
          The league&rsquo;s rules decide every outcome and every dollar. AI only narrates, and a
          fact-checker throws out any number it invents.
        </p>
      </Feature>

      <Feature
        id="front-office"
        eyebrow="Your front office"
        title="Hire people who make you better."
        shot={SHOT.draft}
        flip
      >
        <p>
          Your general manager, head coach, finance director and commercial director each improve a
          part of the club — and a vacant seat costs you.
        </p>
        <p>
          In football and basketball your GM scouts the draft and recommends picks. Draft every pick
          yourself (<strong className="text-paper">Manual</strong>), take your GM&rsquo;s advice (
          <strong className="text-paper">Assisted</strong>) or hand over the board (
          <strong className="text-paper">Auto Draft</strong>). In soccer, you work the transfer
          windows instead.
        </p>
      </Feature>

      <Feature id="books" eyebrow="The books" title="A real balance sheet." shot={SHOT.finances}>
        <p>
          Gate receipts, premium seating, media, sponsorship and merchandise on one side. Payroll,
          staff, venue upkeep and interest on the other. Run a deficit and the league&rsquo;s credit
          facility will cover it — at 9%.
        </p>
      </Feature>

      <Feature
        id="daily"
        eyebrow={product.newspaperName}
        title="Your league has a newspaper."
        shot={SHOT.daily}
        flip
      >
        <p>
          Every week {product.newspaperName} covers what actually happened in your league: results,
          signings, deals, valuations and the standings. When you sign something big, expect a
          headline.
        </p>
      </Feature>

      <section
        id="play"
        aria-labelledby="play-heading"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6"
      >
        <Eyebrow>Play your way</Eyebrow>
        <h2 id="play-heading" className="mt-3 text-3xl sm:text-4xl">
          Alone against the league, or with your friends.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {MODES.map((m) => (
            <article key={m.title} className="panel p-6">
              <h3 className="text-xl">{m.title}</h3>
              <p className="mt-2 text-sm text-silver">{m.body}</p>
            </article>
          ))}
        </div>
      </section>

      <Feature
        id="empire"
        eyebrow="Your legacy"
        title="Build something that lasts."
        shot={SHOT.achievements}
      >
        <p>
          Seasons roll over. Contracts expire, rookies develop, rivals rebuild. Your Owner Score
          tracks how well you run the business, and achievements record the titles, deals and
          turnarounds you pull off.
        </p>
        <p className="text-sm text-slate">
          Coming later: owning franchises in more than one sport at once. Today you build your
          legacy one league at a time.
        </p>
      </Feature>

      <section
        aria-labelledby="join-heading"
        className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6"
      >
        <div className="panel px-6 py-12">
          <h2 id="join-heading" className="text-3xl sm:text-4xl">
            Your franchise is waiting.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-silver">
            {MODE_NOTE[mode]} Plays in your browser on desktop or phone; iOS and Android apps are in
            testing.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={join} className={goldBtn}>
              Become an Owner
            </Link>
            <Link to="/help" className={outlineBtn}>
              Read How to Play
            </Link>
          </div>
        </div>
      </section>

      {stats && stats.owners >= STATS_MIN_OWNERS ? (
        <dl
          className="mx-auto grid max-w-3xl grid-cols-3 gap-4 px-4 pb-8 text-center sm:px-6"
          data-testid="showcase-stats"
        >
          {[
            ['Owners', stats.owners],
            ['Leagues', stats.leagues],
            ['Games played', stats.gamesPlayed],
          ].map(([label, value]) => (
            <div key={label} className="panel p-4">
              <dt className="text-xs uppercase tracking-widest text-slate">{label}</dt>
              <dd className="mt-1 font-display text-3xl">{Number(value).toLocaleString()}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {showcase.data?.updates.length ? (
        <section
          id="updates"
          className="mx-auto max-w-3xl px-4 pb-24 sm:px-6"
          aria-labelledby="updates-heading"
        >
          <h2 id="updates-heading" className="text-2xl">
            Development log
          </h2>
          <p className="mt-1 text-sm text-slate">
            What changed recently, in the team&rsquo;s words.
          </p>
          <ol className="mt-6 space-y-4">
            {showcase.data.updates.map((u) => (
              <li key={u.title} className="border-l-2 border-gold/40 pl-4">
                <p className="font-mono text-xs text-gold-text">{u.date}</p>
                <h3 className="mt-1 text-lg">{u.title}</h3>
                <p className="mt-1 text-sm text-silver">{u.body}</p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </>
  );
}
